import React, { useState, useCallback, useEffect } from "react";
import "./DateRangePicker.scss";

/**
 * Reusable DateRangePicker Component
 * --------------------------------
 * A modern date range picker with preset options and custom calendar
 * 
 * Props:
 * - isOpen: boolean - controls visibility of the picker
 * - dateRange: object - { start: string, end: string }
 * - onDateChange: function - callback when date range changes
 * - onPresetSelect: function - callback when preset is selected (passes preset type to parent)
 * - onClear: function - callback to clear the date range
 * - onToggle: function - callback to toggle the picker visibility
 * - label: string - label for the input
 * - isLoading: boolean - shows loading state
 * - className: string - additional CSS classes
 */
const DateRangePicker = ({
  isOpen,
  dateRange,
  onDateChange,
  onPresetSelect,
  onClear,
  onToggle,
  label,
  isLoading = false,
  className = "",
}) => {
  // UI states
  const [activeTab, setActiveTab] = useState("preset");
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Reset calendar to current month when date range is cleared
  useEffect(() => {
    // If date range is completely cleared (no start, end, or label), reset calendar to current month
    if (!dateRange.start && !dateRange.end && !dateRange.label) {
      setCalendarDate(new Date());
      // Switch to custom tab when clearing, so user can select new dates
      setActiveTab("preset");
    }
  }, [dateRange.start, dateRange.end, dateRange.label]);

  // Handle preset date range selection - just pass the preset type to parent
  const handlePresetDateSelection = useCallback(
    (preset) => {
      if (onPresetSelect) {
        // Let parent component handle the backend API call
        onPresetSelect(preset);
      }
      setActiveTab("preset");
      // Close the dropdown after preset selection
      if (onToggle) {
        onToggle();
      }
    },
    [onPresetSelect, onToggle]
  );

  // Format date for display
  const formatDateRange = (dateRange) => {
    // If we have a preset selected but no custom dates, show the preset label
    if (dateRange.preset && dateRange.label && !dateRange.start && !dateRange.end) {
      return dateRange.label;
    }
    
    // If we have custom dates selected, show them regardless of preset
    if (dateRange.start || dateRange.end) {
      if (!dateRange.start || !dateRange.end) return "Select Date Range";
      return `${dateRange.start} - ${dateRange.end}`;
    }
    
    if (!dateRange.start && !dateRange.end) return "Select Date Range";
    return "Select Date Range";
  };

  // Format display text
  const getDisplayText = (dateRange) => {
    // If we have custom dates selected, prioritize showing them over preset label
    if (dateRange.start || dateRange.end) {
      return formatDateRange(dateRange);
    }
    
    // If we have a preset selected but no custom dates, show the preset label
    if (dateRange.preset && dateRange.label && !dateRange.start && !dateRange.end) {
      return dateRange.label;
    }
    
    return formatDateRange(dateRange);
  };

  // Date selection handlers
  const handleDateSelection = useCallback(
    (day) => {
      // Create full date string in YYYY-MM-DD format
      const fullDate = `${calendarDate.getFullYear()}-${String(
        calendarDate.getMonth() + 1
      ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      const newDateRange = {
        ...dateRange,
        ...(dateRange.start && !dateRange.end
          ? { end: fullDate }
          : { start: fullDate, end: null }),
        // Clear preset properties when user starts selecting custom dates
        preset: null,
        label: null
      };

      onDateChange(newDateRange);

      // Close dropdown when both start and end dates are selected
      if (newDateRange.start && newDateRange.end && onToggle) {
        onToggle();
      }
    },
    [dateRange, onDateChange, calendarDate, onToggle]
  );

  // Get CSS class for date based on selection
  const getDateClass = useCallback(
    (day) => {
      if (!dateRange.start) return "";

      const fullDate = `${calendarDate.getFullYear()}-${String(
        calendarDate.getMonth() + 1
      ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      if (dateRange.end) {
        // Complete range
        if (fullDate === dateRange.start && fullDate === dateRange.end)
          return "selected single";
        if (fullDate === dateRange.start) return "selected start";
        if (fullDate === dateRange.end) return "selected end";
        if (fullDate > dateRange.start && fullDate < dateRange.end)
          return "selected range";
      } else {
        // Only start selected
        if (fullDate === dateRange.start) return "selected start";
      }

      return "";
    },
    [dateRange, calendarDate]
  );

  // Format month/year for display
  const formatMonthYear = useCallback((calendarDate) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return `${months[calendarDate.getMonth()]} ${calendarDate.getFullYear()}`;
  }, []);

  // Navigate months
  const navigateMonth = useCallback(
    (direction) => {
      const newDate = new Date(calendarDate);
      newDate.setMonth(newDate.getMonth() + direction);
      setCalendarDate(newDate);
    },
    [calendarDate]
  );

  // Generate calendar days
  const generateCalendarDays = useCallback(() => {
    const days = [];
    const today = new Date();

    // Get first day of month and number of days
    const firstDay = new Date(
      calendarDate.getFullYear(),
      calendarDate.getMonth(),
      1
    );
    const lastDay = new Date(
      calendarDate.getFullYear(),
      calendarDate.getMonth() + 1,
      0
    );
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Add empty days for the first week
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="day empty"></div>);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        calendarDate.getFullYear(),
        calendarDate.getMonth(),
        day
      );
      const isToday = date.toDateString() === today.toDateString();

      days.push(
        <div
          key={`day-${day}`}
          className={`day ${isToday ? "today" : ""} ${getDateClass(day)}`}
          onClick={() => handleDateSelection(day)}
        >
          {day}
        </div>
      );
    }

    // Add remaining empty days to fill the grid (42 total cells = 6 weeks)
    const totalCells = 42;
    const usedCells = startDayOfWeek + daysInMonth;
    const remainingCells = totalCells - usedCells;

    for (let i = 0; i < remainingCells; i++) {
      days.push(<div key={`empty-end-${i}`} className="day empty"></div>);
    }

    return days;
  }, [calendarDate, getDateClass, handleDateSelection]);

  return (
    <div className={`floating-input-group date-range-picker ${className}`}>
      <div
        className={`date-range-input floating-input ${isOpen ? "open" : ""} ${
          dateRange.start || dateRange.end || dateRange.label ? "has-value" : ""
        }`}
        onClick={onToggle}
      >
        <span>{getDisplayText(dateRange)}</span>
        {!(dateRange.start || dateRange.end || dateRange.label) && !isLoading && (
          <span className="calendar-icon" style={{ color: "#2e8534" }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </span>
        )}
      </div>
      <label className="floating-label">{label}</label>
      {isLoading && (
        <div className="search-loading">
          <div className="simple-spinner"></div>
        </div>
      )}
      {(dateRange.start || dateRange.end || dateRange.label) && !isLoading && (
        <button
          type="button"
          className="clear-input-btn"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          title="Clear Date Range"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      )}
      {isOpen && (
        <div className="modern-date-picker">
          <div className="date-picker-tabs">
            <button
              className={`tab ${activeTab === "preset" ? "active" : ""}`}
              onClick={() => setActiveTab("preset")}
            >
              Preset Ranges
            </button>
            <button
              className={`tab ${activeTab === "custom" ? "active" : ""}`}
              onClick={() => setActiveTab("custom")}
            >
              Custom Range
            </button>
          </div>

          {activeTab === "preset" ? (
            <div className="preset-ranges">
              <div
                className={`preset-option ${dateRange.preset === "today" ? "selected" : ""}`}
                onClick={() => handlePresetDateSelection("today")}
              >
                Today
              </div>
              <div
                className={`preset-option ${dateRange.preset === "yesterday" ? "selected" : ""}`}
                onClick={() => handlePresetDateSelection("yesterday")}
              >
                Yesterday
              </div>
              <div
                className={`preset-option ${dateRange.preset === "week_to_date" ? "selected" : ""}`}
                onClick={() => handlePresetDateSelection("week_to_date")}
              >
                Week To Date
              </div>
              <div
                className={`preset-option ${dateRange.preset === "month_to_date" ? "selected" : ""}`}
                onClick={() => handlePresetDateSelection("month_to_date")}
              >
                Month To Date
              </div>
              <div
                className={`preset-option ${dateRange.preset === "year_to_date" ? "selected" : ""}`}
                onClick={() => handlePresetDateSelection("year_to_date")}
              >
                Year To Date
              </div>
              <div
                className={`preset-option ${dateRange.preset === "last_week" ? "selected" : ""}`}
                onClick={() => handlePresetDateSelection("last_week")}
              >
                Last Week
              </div>
              <div
                className={`preset-option ${dateRange.preset === "last_month" ? "selected" : ""}`}
                onClick={() => handlePresetDateSelection("last_month")}
              >
                Last Month
              </div>
              <div
                className={`preset-option ${dateRange.preset === "last_year" ? "selected" : ""}`}
                onClick={() => handlePresetDateSelection("last_year")}
              >
                Last Year
              </div>
            </div>
          ) : (
            <div className="calendar-container">
              <div className="calendar">
                <div className="calendar-header">
                  <button
                    className="nav-btn"
                    onClick={() => navigateMonth(-1)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <span className="month-year">
                    {formatMonthYear(calendarDate)}
                  </span>
                  <button
                    className="nav-btn"
                    onClick={() => navigateMonth(1)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                <div className="calendar-grid">
                  <div className="day-label">Su</div>
                  <div className="day-label">Mo</div>
                  <div className="day-label">Tu</div>
                  <div className="day-label">We</div>
                  <div className="day-label">Th</div>
                  <div className="day-label">Fr</div>
                  <div className="day-label">Sa</div>

                  {/* Generate calendar days */}
                  {generateCalendarDays()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(DateRangePicker);
